import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../components/navigation/Sidebar';
import PatientContextBar from '../../components/navigation/PatientContextBar';
import QuickActions from '../../components/navigation/QuickActions';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import MedicalHistorySection from './components/MedicalHistorySection';

import MedicationReconciliation from './components/MedicationReconciliation';
import ImmunizationTracker from './components/ImmunizationTracker';
import AllergyManagement from './components/AllergyManagement';
import VitalSignsChart from './components/VitalSignsChart';
import AuditTrailPanel from './components/AuditTrailPanel';
import DiagnosisCodePanel from '../../components/icd/DiagnosisCodePanel';

const PatientRecords = () => {
  const navigate = useNavigate();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilter, setDateFilter] = useState('all');
  const [diagnoses, setDiagnoses] = useState([
    {
      id: 1,
      code: 'E11.9',
      description: 'Type 2 diabetes mellitus without complications',
      category: 'Endocrine',
      status: 'Active',
      diagnosedDate: '2020-01-15',
      notes: 'Patient diagnosed with Type 2 Diabetes Mellitus. Currently managed with oral hypoglycemic agents and lifestyle modifications.'
    },
    {
      id: 2,
      code: 'I10',
      description: 'Essential (primary) hypertension',
      category: 'Cardiovascular',
      status: 'Active',
      diagnosedDate: '2018-03-10',
      notes: 'Essential hypertension diagnosed during routine physical examination. Currently controlled with ACE inhibitor therapy.'
    },
    {
      id: 3,
      code: 'E78.5',
      description: 'Hyperlipidemia, unspecified',
      category: 'Endocrine',
      status: 'Active',
      diagnosedDate: '2017-08-05',
      notes: 'Mixed hyperlipidemia identified through lipid panel screening. Patient started on statin therapy with good response.'
    }
  ]);

  const currentPatient = {
    name: "Sarah Johnson",
    mrn: "MRN-2024-789456",
    dob: "03/15/1985",
    age: "40 years"
  };

  const patientAlerts = [
    {
      id: 1,
      severity: 'critical',
      title: 'Severe Penicillin Allergy',
      message: 'Patient has documented anaphylaxis reaction to penicillin-based medications',
      timestamp: '2 hours ago',
      actionRequired: 'Verify alternative antibiotics before prescribing',
      dismissible: false
    },
    {
      id: 2,
      severity: 'warning',
      title: 'Prescription Interaction Alert',
      message: 'Current prescriptions may interact with newly prescribed anticoagulants',
      timestamp: '5 hours ago',
      actionRequired: 'Review with pharmacist',
      dismissible: true
    }
  ];

  const medicalHistory = [
    {
      id: 1,
      category: 'diagnosis',
      condition: 'Chronic Periodontitis',
      icdCode: 'K05.312',
      description: 'Patient diagnosed with chronic periodontitis, localized, moderate severity. Currently managed with scaling and root planing and periodontal maintenance every 3 months.',
      date: '01/15/2020',
      provider: 'Dr. Michael Chen',
      status: 'Active'
    },
    {
      id: 2,
      category: 'surgery',
      condition: 'Extraction of Tooth #19',
      icdCode: 'K08.1',
      description: 'Surgical extraction of tooth #19 due to severe caries and periapical abscess. Procedure completed without complications. Socket healing well.',
      date: '06/22/2019',
      provider: 'Dr. Jennifer Martinez',
      status: 'Resolved'
    },
    {
      id: 3,
      category: 'diagnosis',
      condition: 'Dental Caries',
      icdCode: 'K02.62',
      description: 'Multiple carious lesions identified on smooth surfaces penetrating into dentin. Teeth #3, #14, and #30 require composite restorations.',
      date: '03/10/2018',
      provider: 'Dr. Michael Chen',
      status: 'Active'
    },
    {
      id: 4,
      category: 'diagnosis',
      condition: 'Gingivitis',
      icdCode: 'K05.10',
      description: 'Chronic gingivitis, plaque induced. Patient counseled on improved oral hygiene techniques. Scheduled for prophylaxis and oral hygiene instruction.',
      date: '08/05/2017',
      provider: 'Dr. Michael Chen',
      status: 'Active'
    }
  ];

  const documents = [
    {
      id: 1,
      type: 'lab',
      name: 'Complete Blood Count - Annual Physical',
      date: '01/10/2026',
      size: 245678,
      version: '1.0',
      uploadedBy: 'Dr. Michael Chen'
    },
    {
      id: 2,
      type: 'imaging',
      name: 'Chest X-Ray - Routine Screening',
      date: '12/15/2025',
      size: 1567890,
      version: '1.0',
      uploadedBy: 'Radiology Department'
    },
    {
      id: 3,
      type: 'note',
      name: 'Progress Note - Diabetes Follow-up',
      date: '01/08/2026',
      size: 89456,
      version: '2.1',
      uploadedBy: 'Dr. Michael Chen'
    },
    {
      id: 4,
      type: 'report',
      name: 'Echocardiogram Report',
      date: '11/20/2025',
      size: 456789,
      version: '1.0',
      uploadedBy: 'Cardiology Department'
    },
    {
      id: 5,
      type: 'consent',
      name: 'Surgical Consent Form - Cholecystectomy',
      date: '06/20/2019',
      size: 123456,
      version: '1.0',
      uploadedBy: 'Surgical Services'
    }
  ];

  const medications = [
    {
      id: 1,
      name: 'Amoxicillin',
      dosage: '500mg three times daily',
      status: 'Active',
      startDate: '01/15/2020',
      prescriber: 'Dr. Michael Chen',
      instructions: 'Take with food. Complete full course of antibiotics for dental infection.',
      refillsRemaining: 0
    },
    {
      id: 2,
      name: 'Ibuprofen',
      dosage: '600mg as needed',
      status: 'Active',
      startDate: '03/10/2018',
      prescriber: 'Dr. Michael Chen',
      instructions: 'Take with food for pain management. Do not exceed 2400mg per day.',
      refillsRemaining: 3
    },
    {
      id: 3,
      name: 'Chlorhexidine Gluconate',
      dosage: '0.12% rinse twice daily',
      status: 'Active',
      startDate: '08/05/2017',
      prescriber: 'Dr. Michael Chen',
      instructions: 'Rinse for 30 seconds after brushing. Do not swallow. May cause temporary staining.',
      refillsRemaining: 2
    },
    {
      id: 4,
      name: 'Fluoride Varnish',
      dosage: 'Applied professionally quarterly',
      status: 'Active',
      startDate: '03/10/2018',
      prescriber: 'Dr. Michael Chen',
      instructions: 'Professional application for caries prevention. Avoid eating for 30 minutes after application.',
      refillsRemaining: 4
    }
  ];

  const immunizations = [
    {
      id: 1,
      vaccine: 'Hepatitis B Vaccine',
      date: '10/15/2025',
      lot: 'HEPB2025-A456',
      site: 'Left Deltoid',
      route: 'Intramuscular',
      administeredBy: 'Nurse Sarah Williams',
      status: 'Complete',
      nextDue: 'Complete Series'
    },
    {
      id: 2,
      vaccine: 'Tdap (Tetanus, Diphtheria, Pertussis)',
      date: '03/20/2024',
      lot: 'TDAP2024-B789',
      site: 'Right Deltoid',
      route: 'Intramuscular',
      administeredBy: 'Nurse Michael Brown',
      status: 'Complete',
      nextDue: '03/20/2034'
    },
    {
      id: 3,
      vaccine: 'COVID-19 Vaccine (mRNA)',
      date: '09/10/2024',
      lot: 'COVID2024-C123',
      site: 'Left Deltoid',
      route: 'Intramuscular',
      administeredBy: 'Nurse Sarah Williams',
      status: 'Complete',
      nextDue: '09/10/2025'
    }
  ];

  const allergies = [
    {
      id: 1,
      allergen: 'Penicillin',
      type: 'Drug',
      severity: 'Severe',
      reaction: 'Anaphylaxis with difficulty breathing, hives, and swelling. Required emergency epinephrine administration.',
      onsetDate: '05/12/2010',
      verifiedBy: 'Dr. Robert Anderson',
      notes: 'Patient carries EpiPen. All penicillin-based antibiotics contraindicated.'
    },
    {
      id: 2,
      allergen: 'Shellfish',
      type: 'Food',
      severity: 'Moderate',
      reaction: 'Urticaria, facial swelling, and gastrointestinal distress within 30 minutes of ingestion.',
      onsetDate: '08/20/2015',
      verifiedBy: 'Dr. Michael Chen',
      notes: 'Patient avoids all shellfish. Cross-contamination risk noted.'
    },
    {
      id: 3,
      allergen: 'Latex',
      type: 'Environmental',
      severity: 'Mild',
      reaction: 'Contact dermatitis with localized redness and itching at site of exposure.',
      onsetDate: '02/15/2018',
      verifiedBy: 'Dr. Jennifer Martinez',
      notes: 'Use non-latex gloves during all procedures. Latex-free environment required.'
    }
  ];

  const vitals = [
    {
      id: 1,
      type: 'Blood Pressure',
      value: '128/82',
      timestamp: '01/14/2026 09:30 AM',
      flag: 'Normal'
    },
    {
      id: 2,
      type: 'Heart Rate',
      value: '76 bpm',
      timestamp: '01/14/2026 09:30 AM',
      flag: 'Normal'
    },
    {
      id: 3,
      type: 'Temperature',
      value: '98.6°F',
      timestamp: '01/14/2026 09:30 AM',
      flag: 'Normal'
    },
    {
      id: 4,
      type: 'Respiratory Rate',
      value: '16/min',
      timestamp: '01/14/2026 09:30 AM',
      flag: 'Normal'
    },
    {
      id: 5,
      type: 'Oxygen Saturation',
      value: '98%',
      timestamp: '01/14/2026 09:30 AM',
      flag: 'Normal'
    },
    {
      id: 6,
      type: 'Weight',
      value: '165 lbs',
      timestamp: '01/14/2026 09:30 AM',
      flag: 'Normal'
    }
  ];

  const auditLogs = [
    {
      id: 1,
      action: 'view',
      description: 'Viewed patient medical history',
      user: 'Dr. Michael Chen',
      role: 'Primary Care Physician',
      timestamp: '01/14/2026 10:45 AM',
      ipAddress: '192.168.1.105'
    },
    {
      id: 2,
      action: 'edit',
      description: 'Updated medication dosage for Metformin',
      user: 'Dr. Michael Chen',
      role: 'Primary Care Physician',
      timestamp: '01/14/2026 10:30 AM',
      ipAddress: '192.168.1.105'
    },
    {
      id: 3,
      action: 'create',
      description: 'Added new allergy record for Latex',
      user: 'Nurse Sarah Williams',
      role: 'Registered Nurse',
      timestamp: '01/14/2026 09:15 AM',
      ipAddress: '192.168.1.112'
    },
    {
      id: 4,
      action: 'view',
      description: 'Accessed lab results from 01/10/2026',
      user: 'Dr. Jennifer Martinez',
      role: 'Specialist',
      timestamp: '01/13/2026 03:20 PM',
      ipAddress: '192.168.1.108'
    },
    {
      id: 5,
      action: 'edit',
      description: 'Modified vital signs entry',
      user: 'Nurse Michael Brown',
      role: 'Registered Nurse',
      timestamp: '01/13/2026 02:45 PM',
      ipAddress: '192.168.1.115'
    }
  ];

  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'LayoutDashboard' },
    { id: 'diagnoses', label: 'Diagnoses', icon: 'Stethoscope' },
    { id: 'medications', label: 'Medications', icon: 'Pill' },
    { id: 'allergies', label: 'Allergies', icon: 'AlertTriangle' },
    { id: 'vitals', label: 'Vital Signs', icon: 'Activity' },
    { id: 'immunizations', label: 'Immunizations', icon: 'Syringe' },
    { id: 'documents', label: 'Documents', icon: 'FileText' },
    { id: 'audit', label: 'Audit Trail', icon: 'Shield' }
  ];

  const dateFilterOptions = [
    { value: 'all', label: 'All Time' },
    { value: 'today', label: 'Today' },
    { value: 'week', label: 'This Week' },
    { value: 'month', label: 'This Month' },
    { value: 'year', label: 'This Year' }
  ];

  const quickActions = [
    {
      id: 'add-note',
      label: 'Add Note',
      icon: 'FileText',
      onClick: () => navigate('/clinical-documentation')
    },
    {
      id: 'order-lab',
      label: 'Order Lab',
      icon: 'FlaskConical',
      onClick: () => navigate('/lab-results')
    },
    {
      id: 'prescribe',
      label: 'Prescribe',
      icon: 'Pill',
      onClick: () => navigate('/medication-management')
    },
    {
      id: 'schedule',
      label: 'Schedule',
      icon: 'Calendar',
      onClick: () => navigate('/appointment-scheduling')
    }
  ];

  const handleAlertClick = (alert) => {
    console.log('Alert clicked:', alert);
  };

  const handleAddDiagnosis = (diagnosis) => {
    setDiagnoses([...diagnoses, diagnosis]);
  };

  const handleRemoveDiagnosis = (id) => {
    setDiagnoses(diagnoses?.filter(d => d?.id !== id));
  };

  const handleUpdateDiagnosis = (id, updates) => {
    setDiagnoses(diagnoses?.map(d => d?.id === id ? { ...d, ...updates } : d));
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-card rounded-xl border border-border shadow-elevation-2 p-6">
                <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                  <Icon name="User" size={20} />
                  Patient Demographics
                </h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Full Name</span>
                    <span className="text-sm font-medium text-foreground">Sarah Johnson</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Date of Birth</span>
                    <span className="text-sm font-medium text-foreground">03/15/1978</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Age</span>
                    <span className="text-sm font-medium text-foreground">48 years</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Gender</span>
                    <span className="text-sm font-medium text-foreground">Female</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Blood Type</span>
                    <span className="text-sm font-medium text-foreground">O+</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">MRN</span>
                    <span className="text-sm font-medium text-foreground font-mono">MRN-2024-789456</span>
                  </div>
                </div>
              </div>

              <div className="bg-card rounded-xl border border-border shadow-elevation-2 p-6">
                <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                  <Icon name="Phone" size={20} />
                  Contact Information
                </h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Phone</span>
                    <span className="text-sm font-medium text-foreground">(555) 123-4567</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Email</span>
                    <span className="text-sm font-medium text-foreground">sarah.j@email.com</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Address</span>
                    <span className="text-sm font-medium text-foreground text-right">123 Main St, Apt 4B&lt;br/&gt;New York, NY 10001</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Emergency Contact</span>
                    <span className="text-sm font-medium text-foreground">John Johnson</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Emergency Phone</span>
                    <span className="text-sm font-medium text-foreground">(555) 987-6543</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-card rounded-xl border border-border shadow-elevation-2 p-6">
              <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                <Icon name="FileText" size={20} />
                Medical History Summary
              </h3>
              <MedicalHistorySection history={medicalHistory} />
            </div>

            <div className="bg-card rounded-xl border border-border shadow-elevation-2 p-6">
              <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                <Icon name="Stethoscope" size={20} />
                Active Diagnoses
              </h3>
              <div className="space-y-2">
                {diagnoses?.filter(d => d?.status === 'Active')?.map((diagnosis) => (
                  <div key={diagnosis?.id} className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                    <Icon name="CheckCircle" size={16} className="text-success" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-foreground">
                        <span className="font-mono">{diagnosis?.code}</span> - {diagnosis?.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );
      case 'diagnoses':
        return <DiagnosisCodePanel diagnoses={diagnoses} onAdd={handleAddDiagnosis} onRemove={handleRemoveDiagnosis} onUpdate={handleUpdateDiagnosis} />;
      case 'medications':
        return <MedicationReconciliation medications={medications} />;
      case 'immunizations':
        return <ImmunizationTracker immunizations={immunizations} />;
      case 'allergies':
        return <AllergyManagement allergies={allergies} />;
      case 'vitals':
        return <VitalSignsChart vitals={vitals} />;
      case 'audit':
        return <AuditTrailPanel auditLogs={auditLogs} />;
      default:
        return <MedicalHistorySection history={medicalHistory} />;
    }
  };

  return (
    <div className="flex h-screen bg-background overflow-hidden pt-16">
      <Sidebar
        isCollapsed={isSidebarCollapsed}
        onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
      />

      <div className="flex-1 flex flex-col overflow-hidden">
        <PatientContextBar
          patient={currentPatient}
          alerts={patientAlerts}
          onAlertClick={handleAlertClick}
        />

        <main className="flex-1 overflow-y-auto">
          <div className="max-w-screen-2xl mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6 md:mb-8">
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-foreground flex items-center gap-3">
                  <Icon name="FileText" size={32} className="text-primary" />
                  Patient Records
                </h1>
                <p className="text-sm md:text-base text-muted-foreground mt-1">
                  Comprehensive dental health records, treatment history, and patient information
                </p>
              </div>
              <Button
                variant="default"
                iconName="Download"
                iconPosition="left"
              >
                Export Records
              </Button>
            </div>

            <div className="flex flex-col lg:flex-row gap-4 mb-6">
              <div className="flex-1">
                <Input
                  type="search"
                  placeholder="Search patient records..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e?.target?.value)}
                />
              </div>
              <div className="w-full lg:w-48">
                <Select
                  options={dateFilterOptions}
                  value={dateFilter}
                  onChange={setDateFilter}
                  placeholder="Filter by date"
                />
              </div>
            </div>

            <div className="bg-card rounded-xl shadow-elevation-2 border border-border overflow-hidden">
              <div className="border-b border-border overflow-x-auto">
                <div className="flex min-w-max lg:min-w-0">
                  {tabs?.map((tab) => (
                    <button
                      key={tab?.id}
                      onClick={() => setActiveTab(tab?.id)}
                      className={`flex items-center gap-2 px-4 md:px-6 py-3 md:py-4 text-sm md:text-base font-medium transition-smooth flex-shrink-0 ${
                        activeTab === tab?.id
                          ? 'text-primary border-b-2 border-primary bg-primary/5' :'text-muted-foreground hover:text-foreground hover:bg-muted'
                      }`}
                    >
                      <Icon name={tab?.icon} size={18} />
                      <span>{tab?.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="p-4 md:p-6 lg:p-8">
                {renderTabContent()}
              </div>
            </div>
          </div>
        </main>
      </div>
      <QuickActions actions={quickActions} position="bottom-right" />
    </div>
  );
};

export default PatientRecords;